import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Copy, Loader2, Percent, Search, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { money } from "@/components/Billing";

const input = "bg-[var(--card)] border border-[var(--line)] focus:border-[var(--cyan)] outline-none px-3 py-2 text-sm transition-colors";
const label = "font-mono text-[9px] uppercase tracking-widest text-[var(--muted)]";

const DiscountCell = ({ c, onSaved }) => {
  const [pct, setPct] = useState(c.discount?.percent ?? "");
  const [note, setNote] = useState(c.discount?.note ?? "");
  const [busy, setBusy] = useState(false);
  const save = async (percent) => {
    setBusy(true);
    try { await api.put(`/admin/clients/${c.user_id}/discount`, { percent: Number(percent) || 0, note }); toast.success(Number(percent) ? `${c.email} now gets ${percent}% off` : "Discount removed"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center border border-[var(--line)] bg-[var(--card)] w-20">
        <input data-testid={`discount-input-${c.user_id}`} type="number" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="0" className="w-full bg-transparent outline-none px-2 py-1.5 text-sm font-mono" />
        <Percent size={11} className="text-[var(--muted)] mr-2 shrink-0" />
      </div>
      <input data-testid={`discount-note-${c.user_id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" className="w-28 bg-[var(--card)] border border-[var(--line)] outline-none px-2 py-1.5 text-xs" />
      <button data-testid={`discount-save-${c.user_id}`} disabled={busy} onClick={() => save(pct)} className="px-2.5 py-1.5 text-xs bg-[var(--cyan)] text-[var(--bg)] font-semibold disabled:opacity-50">Save</button>
      {c.discount && <button data-testid={`discount-clear-${c.user_id}`} disabled={busy} onClick={() => { setPct(""); save(0); }} className="text-xs text-[var(--muted)] hover:text-red-400">Clear</button>}
    </div>
  );
};

const Clients = () => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/clients", { params: { q } }).then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e))), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return (
    <section className="border border-[var(--line)] bg-[var(--surface)] p-5 sm:p-6 min-w-0" data-testid="admin-clients">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display font-semibold text-lg">Clients & discounts</h2>
        <div className="flex items-center gap-2 bg-[var(--card)] border border-[var(--line)] px-3 w-full sm:w-72">
          <Search size={14} className="text-[var(--muted)]" />
          <input data-testid="client-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="flex-1 bg-transparent outline-none py-2 text-sm" />
        </div>
      </div>
      <p className="mt-2 text-xs text-[var(--muted)]">A client discount applies automatically to every draft, unlock and size upgrade they pay for. It doesn't stack with codes - whichever discount is bigger applies.</p>
      <div className="mt-5 overflow-x-auto">
        {!rows ? <Loader2 className="animate-spin text-[var(--cyan)]" /> : (
          <table className="w-full min-w-[760px] text-sm">
            <thead><tr className={`${label} text-left border-b border-[var(--line)]`}>
              <th className="pb-2 font-normal">Client</th><th className="pb-2 font-normal">Card</th><th className="pb-2 font-normal text-right pr-4">Drafts</th><th className="pb-2 font-normal text-right pr-4">Spent</th><th className="pb-2 font-normal">Discount</th>
            </tr></thead>
            <tbody>{rows.map((c) => (
              <tr key={c.user_id} className="border-b border-[var(--line)]/60" data-testid={`client-row-${c.user_id}`}>
                <td className="py-3 pr-4"><p className="truncate max-w-[220px]">{c.name || "—"}</p><p className="text-xs text-[var(--muted)] truncate max-w-[220px]">{c.email}</p></td>
                <td className="py-3 pr-4 font-mono text-xs">{c.has_card ? <span className="text-[var(--emerald)]">on file</span> : <span className="text-[var(--muted)]">none</span>}</td>
                <td className="py-3 pr-4 text-right font-mono text-xs">{c.projects}</td>
                <td className="py-3 pr-4 text-right font-mono text-xs">{money(c.spent)}</td>
                <td className="py-3"><DiscountCell c={c} onSaved={load} /></td>
              </tr>
            ))}</tbody>
          </table>
        )}
        {rows?.length === 0 && <p className="py-6 text-sm text-[var(--muted)]" data-testid="clients-empty">No clients found.</p>}
      </div>
    </section>
  );
};

const Field = ({ k, children, w = "" }) => <label className={`flex flex-col gap-1 ${w}`}><span className={label}>{k}</span>{children}</label>;

const BLANK = { type: "promo", kind: "percent", value: 10, count: 1, prefix: "", custom_code: "", max_uses: 1, expires_at: "", label: "" };

const Generator = ({ onCreated }) => {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, value: Number(f.value), count: Number(f.count), max_uses: Number(f.max_uses), expires_at: f.expires_at ? new Date(`${f.expires_at}T23:59:59`).toISOString() : null };
      const { data } = await api.post("/admin/codes", body);
      toast.success(`${data.codes.length} code${data.codes.length > 1 ? "s" : ""} created`);
      onCreated(data.codes);
      setF({ ...BLANK, type: f.type, kind: f.kind });
    } catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="code-generator">
      <Field k="Type"><select data-testid="code-type-select" value={f.type} onChange={set("type")} className={input}><option value="promo">Promotional code</option><option value="discount">Discount code</option></select></Field>
      <Field k="Discount"><select data-testid="code-kind-select" value={f.kind} onChange={set("kind")} className={input}><option value="percent">% off</option><option value="amount">$ off</option></select></Field>
      <Field k={f.kind === "percent" ? "Percent" : "Dollars"}><input data-testid="code-value-input" type="number" min="1" value={f.value} onChange={set("value")} className={input} /></Field>
      <Field k="How many codes"><input data-testid="code-count-input" type="number" min="1" max="50" value={f.count} onChange={set("count")} className={input} /></Field>
      <Field k="Prefix (optional)"><input data-testid="code-prefix-input" value={f.prefix} onChange={set("prefix")} placeholder="SPRING" className={`${input} uppercase`} /></Field>
      <Field k="Or exact code"><input data-testid="code-custom-input" value={f.custom_code} onChange={set("custom_code")} placeholder="WELCOME25" className={`${input} uppercase`} /></Field>
      <Field k="Uses per code (0 = unlimited)"><input data-testid="code-max-uses-input" type="number" min="0" value={f.max_uses} onChange={set("max_uses")} className={input} /></Field>
      <Field k="Expires (optional)"><input data-testid="code-expires-input" type="date" value={f.expires_at} onChange={set("expires_at")} className={input} /></Field>
      <Field k="Label (shown at checkout)" w="col-span-2 sm:col-span-3"><input data-testid="code-label-input" value={f.label} onChange={set("label")} placeholder="Spring promotion" className={input} /></Field>
      <div className="flex items-end"><button data-testid="generate-codes-btn" disabled={busy} className="w-full py-2.5 bg-[var(--amber)] text-[var(--bg)] font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate</button></div>
    </form>
  );
};

const Codes = () => {
  const [codes, setCodes] = useState(null);
  const [fresh, setFresh] = useState([]);
  const load = useCallback(() => api.get("/admin/codes").then(({ data }) => setCodes(data)), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (c) => { try { await api.patch(`/admin/codes/${c.code}`, { active: !c.active }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (c) => { if (!window.confirm(`Delete ${c.code}?`)) return; await api.delete(`/admin/codes/${c.code}`); setFresh((f) => f.filter((x) => x !== c.code)); load(); };
  const copy = async (text) => { await navigator.clipboard.writeText(text); toast.success("Copied"); };
  const off = (c) => (c.kind === "percent" ? `${c.value}% off` : `${money(c.value)} off`);
  return (
    <section className="border border-[var(--line)] bg-[var(--surface)] p-5 sm:p-6 min-w-0" data-testid="admin-codes">
      <h2 className="font-display font-semibold text-lg">Promotional & discount codes</h2>
      <p className="mt-2 mb-5 text-xs text-[var(--muted)]">Clients type these at checkout. Each person can use a given code once.</p>
      <Generator onCreated={(c) => { setFresh(c.map((x) => x.code)); load(); }} />
      {fresh.length > 0 && (
        <div className="mt-4 p-3 border border-[var(--emerald)]/40 bg-[var(--emerald)]/5 flex flex-wrap items-center gap-2" data-testid="fresh-codes">
          {fresh.map((c) => <span key={c} className="font-mono text-xs px-2 py-1 bg-[var(--card)]">{c}</span>)}
          <button data-testid="copy-fresh-codes-btn" onClick={() => copy(fresh.join("\n"))} className="ml-auto text-xs text-[var(--cyan)] flex items-center gap-1"><Copy size={12} /> Copy all</button>
        </div>
      )}
      <div className="mt-6 overflow-x-auto">
        {!codes ? <Loader2 className="animate-spin text-[var(--cyan)]" /> : codes.length === 0 ? <p className="text-sm text-[var(--muted)]" data-testid="codes-empty">No codes yet.</p> : (
          <table className="w-full min-w-[720px] text-sm">
            <thead><tr className={`${label} text-left border-b border-[var(--line)]`}>
              <th className="pb-2 font-normal">Code</th><th className="pb-2 font-normal">Type</th><th className="pb-2 font-normal">Value</th><th className="pb-2 font-normal">Used</th><th className="pb-2 font-normal">Expires</th><th className="pb-2 font-normal">Status</th><th />
            </tr></thead>
            <tbody>{codes.map((c) => (
              <tr key={c.code} className="border-b border-[var(--line)]/60" data-testid={`code-row-${c.code}`}>
                <td className="py-2.5 pr-3"><button onClick={() => copy(c.code)} className="font-mono text-xs hover:text-[var(--cyan)] flex items-center gap-1.5">{c.code} <Copy size={11} /></button>{c.label && <p className="text-[11px] text-[var(--muted)]">{c.label}</p>}</td>
                <td className="py-2.5 pr-3 text-xs">{c.type === "promo" ? "Promotional" : "Discount"}</td>
                <td className="py-2.5 pr-3 font-mono text-xs">{off(c)}</td>
                <td className="py-2.5 pr-3 font-mono text-xs">{c.uses}{c.max_uses ? ` / ${c.max_uses}` : " / ∞"}</td>
                <td className="py-2.5 pr-3 font-mono text-xs text-[var(--muted)]">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</td>
                <td className="py-2.5 pr-3"><button data-testid={`code-toggle-${c.code}`} onClick={() => toggle(c)} className={`font-mono text-[10px] uppercase tracking-widest ${c.active ? "text-[var(--emerald)]" : "text-[var(--muted)]"}`}>{c.active ? "Active" : "Paused"}</button></td>
                <td className="py-2.5 text-right"><button data-testid={`code-delete-${c.code}`} onClick={() => del(c)} className="text-[var(--muted)] hover:text-red-400"><Trash2 size={14} /></button></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </section>
  );
};

export default function AdminPanel() {
  const { user } = useAuth();
  useEffect(() => { document.title = "Admin · RhinoDraft AI"; }, []);
  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return (
    <div className="min-h-screen blueprint-grid">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-6 py-14 space-y-8" data-testid="admin-panel">
        <div className="rise">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--amber)]">Admin</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight">Pricing control</h1>
        </div>
        <Clients />
        <Codes />
      </main>
    </div>
  );
}
